import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getDeviceTimezone } from '../utils/time';
import api, { ApiError } from '../services/api';
import { CURRENCIES, currencySymbol } from '../constants/currency';
import { SUPPORTED_LANGUAGES, normalizeLang } from '../i18n';
import i18n from '../i18n';

// Часто используемые IANA-пояса для выбора вручную.
const TZ_OPTIONS = [
  'Europe/Kaliningrad', 'Europe/Moscow', 'Europe/Samara', 'Asia/Yekaterinburg',
  'Asia/Omsk', 'Asia/Novosibirsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk',
  'Asia/Yakutsk', 'Asia/Vladivostok', 'Asia/Magadan', 'Asia/Kamchatka',
  'Europe/Kyiv', 'Europe/Minsk', 'Europe/Tallinn', 'Europe/Riga', 'Europe/Vilnius',
  'Asia/Almaty', 'Asia/Tashkent', 'Asia/Bishkek', 'Asia/Dushanbe', 'Asia/Ashgabat',
  'Asia/Baku', 'Asia/Yerevan', 'Asia/Tbilisi', 'Europe/Berlin', 'Europe/London', 'UTC',
];

export default function AccountPage() {
  const { user, permissions, logout, upgradeToSchoolOwner, setUserTimezone, setUserCurrency, setUserLanguage, setSchoolTimezone } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Upgrade to owner_school ─────────────────────────────────────────
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [upgradeDone, setUpgradeDone] = useState<string | null>(null); // имя созданной школы
  const isUpgradeable = user?.role === 'user';

  // ── Billing plan (тариф и подписка) ─────────────────────────────────
  const [myPlan, setMyPlan] = useState<string | null>(null);
  const [isSysOwner, setIsSysOwner] = useState(false);
  useEffect(() => {
    api.getMyBillingPlan()
      .then(ent => {
        setMyPlan(ent.plan?.name || ent.plan?.id || null);
        setIsSysOwner(!!ent.is_system_owner);
      })
      .catch(() => {
        setMyPlan(null);
        setIsSysOwner(false);
      });
  }, []);

  const closeUpgradeModal = useCallback(() => {
    if (upgrading) return;
    setShowUpgradeModal(false);
    setSchoolName('');
    setUpgradeError(null);
    setUpgradeDone(null);
  }, [upgrading]);

  // Закрытие модалки по Escape
  useEffect(() => {
    if (!showUpgradeModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeUpgradeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showUpgradeModal, closeUpgradeModal]);

  const handleUpgrade = async () => {
    if (!schoolName.trim()) {
      setUpgradeError('Введите название школы');
      return;
    }
    setUpgrading(true);
    setUpgradeError(null);
    try {
      await upgradeToSchoolOwner(schoolName.trim());
      setUpgradeDone(schoolName.trim());
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setUpgradeError('Вы уже владелец школы');
      } else {
        setUpgradeError('Не удалось создать школу. Проверьте соединение и попробуйте ещё раз.');
      }
    } finally {
      setUpgrading(false);
    }
  };

  const telegramUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

  // ── Notifications state ────────────────────────────────────────────
  const notificationsSupported = (() => {
    try { return typeof Notification !== 'undefined' && 'requestPermission' in Notification; }
    catch { return false; }
  })();

  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    try {
      return localStorage.getItem('edupulse_notifications') === 'true'
        && notificationsSupported
        && Notification.permission === 'granted';
    } catch { return false; }
  });

  const toggleNotifications = async () => {
    if (!notificationsSupported) return; // Просто игнорируем, без алерта

    const next = !notificationsEnabled;

    if (next) {
      if (Notification.permission === 'denied') {
        return; // Игнорируем, без алерта
      }

      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return; // Игнорируем, без алерта
        }
      }

      // Показываем тестовое уведомление
      try {
        new Notification('🔔 EduPulse', {
          body: 'Уведомления включены! Теперь вы будете получать напоминания о занятиях и платежах.',
        });
      } catch {}

      setNotificationsEnabled(true);
      localStorage.setItem('edupulse_notifications', 'true');
    } else {
      setNotificationsEnabled(false);
      localStorage.setItem('edupulse_notifications', 'false');
    }
  };

  // Статус уведомлений для отображения (с защитой от ошибок)
  const notificationStatus = (() => {
    try {
      if (!notificationsSupported) return 'Не поддерживается';
      if (notificationsEnabled) return 'Включены';
      if (Notification.permission === 'denied') return 'Запрещены в браузере';
      return 'Отключены';
    } catch { return 'Недоступно'; }
  })();

  // ── User info ──────────────────────────────────────────────────────
  // const roleLabels: Record<string, { label: string; color: string; icon: string }> = {
  //   owner: { label: 'Владелец', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: '👑' },
  //   admin: { label: 'Администратор', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: '🛡️' },
  //   tester: { label: 'Тестировщик', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: '🧪' },
  //   user: { label: 'Пользователь', color: 'bg-gray-100 text-gray-700 border-gray-200', icon: '👤' },
  //   owner_school: { label: 'Владелец школы', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: '🏫' },
  //   teacher: { label: 'Учитель', color: 'bg-teal-100 text-teal-700 border-teal-200', icon: '👩🏫' },
  // };
  const roleLabels: Record<
    string,
    { label: string; color: string; icon: React.ReactNode }
  > = {
    owner: {
      label: 'Владелец',
      color: 'bg-purple-100 text-purple-700 border-purple-200',
      icon: <img src="/icons/role_owner.svg" alt="owner" width="18" height="18" className="object-contain shrink-0" />,
    },

    admin: {
      label: 'Администратор',
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      icon: <img src="/icons/role_admin.svg" alt="admin" width="18" height="18" className="object-contain shrink-0" />,
    },

    tester: {
      label: 'Тестировщик',
      color: 'bg-amber-100 text-amber-700 border-amber-200',
      icon: <img src="/icons/role_tester.svg" alt="tester" width="18" height="18" className="object-contain shrink-0" />,
    },

    user: {
      label: 'Пользователь',
      color: 'bg-gray-100 text-gray-700 border-gray-200',
      icon: <img src="/icons/role_user.svg" alt="user" width="18" height="18" className="object-contain shrink-0" />,
    },

    owner_school: {
      label: 'Владелец школы',
      color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      icon: <img src="/icons/role_owner-school.svg" alt="owner_school" width="18" height="18" className="object-contain shrink-0" />,
    },

    teacher: {
      label: 'Учитель',
      color: 'bg-teal-100 text-teal-700 border-teal-200',
      icon: <img src="/icons/role_teacher.svg" alt="teacher" width="18" height="18" className="object-contain shrink-0" />,
    },
  };

  const roleInfo = roleLabels[user?.role || 'user'];
  const firstName = telegramUser?.first_name || user?.first_name || '';
  const lastName = telegramUser?.last_name || user?.last_name || '';
  const displayUsername = telegramUser?.username || user?.username;
  const displayPhoto = telegramUser?.photo_url || user?.photo_url;
  const displayTelegramId = telegramUser?.id || user?.telegram_id;
  const displayName = [firstName, lastName].filter(Boolean).join(' ')
    || (displayUsername ? `@${displayUsername}` : 'Telegram User');

  // ── Settings items ─────────────────────────────────────────────────
  // ── Notification status for icon (safe, uses pre-computed value) ────
  const notificationIcon = !notificationsSupported
    ? '📵'
    : <img src="/icons/уведомления.svg" alt="notifications" width="18" height="18" className="object-contain" />;

  // ── Timezone ────────────────────────────────────────────────────────
  const detectedTz = getDeviceTimezone();
  const [selectedTz, setSelectedTz] = useState(user?.timezone || detectedTz);
  const [savingTz, setSavingTz] = useState(false);
  const [tzMsg, setTzMsg] = useState<string | null>(null);
  // Текущее локальное время (обновляется каждые 30 сек)
  const [clockNow, setClockNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setClockNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  const localTime = `${String(clockNow.getHours()).padStart(2, '0')}:${String(clockNow.getMinutes()).padStart(2, '0')}`;

  const handleSaveTimezone = async () => {
    setSavingTz(true);
    setTzMsg(null);
    try {
      const tz = selectedTz.trim();
      await setUserTimezone(tz);
      // Для владельца школы пояс также становится поясом школы — занятия
      // всей команды показываются в нём (каждый видит их в своём локальном).
      if (user?.role === 'owner_school' && tz) {
        await setSchoolTimezone(tz);
      }
      setTzMsg('✓ Сохранено');
    } catch {
      setTzMsg('Не удалось сохранить');
    } finally {
      setSavingTz(false);
      setTimeout(() => setTzMsg(null), 3000);
    }
  };

  // ── Currency (валюта ученических платежей) ────────────────────────
  const [selectedCurrency, setSelectedCurrency] = useState(user?.currency || 'KZT');
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [currencyMsg, setCurrencyMsg] = useState<string | null>(null);

  const handleSaveCurrency = async () => {
    setSavingCurrency(true);
    setCurrencyMsg(null);
    try {
      await setUserCurrency(selectedCurrency);
      setCurrencyMsg('✓ Сохранено');
    } catch {
      setCurrencyMsg('Не удалось сохранить');
    } finally {
      setSavingCurrency(false);
      setTimeout(() => setCurrencyMsg(null), 3000);
    }
  };

  // ── Language (язык интерфейса) ────────────────────────────────────
  const [selectedLang, setSelectedLang] = useState(normalizeLang(user?.language_code || i18n.language));
  const [savingLang, setSavingLang] = useState(false);
  const [langMsg, setLangMsg] = useState<string | null>(null);

  const handleSaveLanguage = async () => {
    setSavingLang(true);
    setLangMsg(null);
    try {
      await setUserLanguage(selectedLang);
      setLangMsg('✓ Сохранено');
    } catch {
      setLangMsg('Не удалось сохранить');
    } finally {
      setSavingLang(false);
      setTimeout(() => setLangMsg(null), 3000);
    }
  };

  // ── Settings accordion state ──────────────────────────────────────
  const [settingsOpen, setSettingsOpen] = useState(false);

  // «Настройки» — только личные настройки (уведомления, тема).
  // Тариф и админ-ссылки сюда не входят (по желанию Тамилы 18.08.2026).
  const settingsItems = [
    {
      icon: notificationIcon,
      label: 'Уведомления',
      description: notificationStatus,
      action: toggleNotifications,
      type: 'toggle' as const,
      value: notificationsEnabled,
    },
    {
      icon: <img src="/icons/dark-theme.svg" alt="theme" width="18" height="18" className="object-contain" />,
      label: 'Тёмная тема',
      description: theme === 'dark' ? 'Включена' : 'Отключена',
      action: toggleTheme,
      type: 'toggle' as const,
      value: theme === 'dark',
    },
  ];

  return (
    <div className="p-4 space-y-4 animate-fade-in">
      {/* ── Profile Card ───────────────────────────────────────────── */}
      <div className="tg-card flex flex-col items-center py-6 px-4 relative overflow-hidden">
        {/* Decorative gradient blob */}
        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br from-[var(--tg-theme-button-color)]/10 to-purple-500/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full bg-gradient-to-tr from-blue-500/10 to-[var(--tg-theme-button-color)]/10 blur-2xl" />

        <div className="relative z-10 flex flex-col items-center">
          {displayPhoto ? (
            <img
              src={displayPhoto}
              alt=""
              className="w-20 h-20 rounded-full mb-3 border-2 border-[var(--tg-theme-button-color)] object-cover shadow-md"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[var(--tg-theme-button-color)] to-purple-600 flex items-center justify-center text-3xl text-white mb-3 shadow-lg">
              {displayName[0]}
            </div>
          )}
          <h2 className="text-xl font-bold text-[var(--tg-theme-text-color)]">
            {displayName}
          </h2>
          {displayUsername && (
            <span className="text-sm text-[var(--tg-theme-hint-color)] mt-0.5">
              @{displayUsername}
            </span>
          )}
          {displayTelegramId && (
            <span className="text-[10px] text-[var(--tg-theme-hint-color)] mt-0.5 opacity-50 font-mono">
              ID: {displayTelegramId}
            </span>
          )}
          <div className={`mt-3 px-4 py-1.5 rounded-full text-sm font-medium border flex items-center gap-1.5 ${roleInfo.color}`}>
            <span>{roleInfo.icon}</span>
            <span>{roleInfo.label}</span>
          </div>
        </div>
      </div>

      {/* ── Upgrade to school owner (stub) ─────────────────────────── */}
      {isUpgradeable && (
        <div className="tg-card relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full bg-gradient-to-br from-purple-500/15 to-[var(--tg-theme-button-color)]/15 blur-2xl" />
          <div className="relative z-10">
            <h3 className="text-base font-semibold text-[var(--tg-theme-text-color)] mb-1 flex items-center gap-2">
              <span>🏫</span> Ваша команда
            </h3>
            <p className="text-xs text-[var(--tg-theme-hint-color)] leading-relaxed mb-3">
              Управляйте школой вместе с командой: назначайте учителей и администраторов.
            </p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[var(--tg-theme-button-color)] to-purple-600 text-[var(--tg-theme-button-text-color)] font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <img src="/icons/role_owner-school.svg" alt="owner-school" width="18" height="18" className="object-contain" /> Стать владельцем школы
            </button>
          </div>
        </div>
      )}

      {/* ── Upgrade modal ───────────────────────────────────────────── */}
      {showUpgradeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm"
          onClick={closeUpgradeModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Стать владельцем школы"
            className="tg-card w-full max-w-sm p-5 animate-fade-in max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {upgradeDone ? (
              /* ── Success ── */
              <div className="text-center">
                <div className="text-4xl mb-3">🎉</div>
                <h3 className="text-base font-bold text-[var(--tg-theme-text-color)] mb-2">
                  Школа «{upgradeDone}» создана!
                </h3>
                <p className="text-xs text-[var(--tg-theme-hint-color)] leading-relaxed mb-4">
                  Теперь вы владелец школы. Ваши ученики, курсы и платежи остались с вами.
                  Скоро появится раздел «Команда», чтобы приглашать учителей и администраторов.
                </p>
                <button
                  onClick={closeUpgradeModal}
                  className="w-full py-2.5 rounded-xl bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] font-semibold text-sm transition-all active:scale-[0.98]"
                >
                  Отлично
                </button>
              </div>
            ) : (
              /* ── Form ── */
              <>
                {/* <div className="text-4xl mb-3 text-center">👑</div> */}
                <div className="mb-3 flex justify-center">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 512 512"
                    xmlns="http://www.w3.org/2000/svg"
                    className="shrink-0"
                  >
                    <path
                      fill="#F89021"
                      d="M256,238.812l-99.098-116.105c-6.955-8.148-19.546-8.148-26.501,0L31.304,238.812H256z"
                    />
                    <path
                      opacity="0.15"
                      d="M188.917,160.216l-29.983,35.129c-8.021,9.398-22.543,9.398-30.564,0l-29.983-35.129l-67.083,78.596H256L188.917,160.216z"
                    />
                    <path
                      fill="#F89021"
                      d="M480.696,238.812l-99.098-116.105c-6.955-8.148-19.546-8.148-26.501,0L256,238.812H480.696z"
                    />
                    <path
                      opacity="0.15"
                      d="M413.614,160.216l-29.983,35.129c-8.021,9.398-22.543,9.398-30.564,0l-29.983-35.129L256,238.812h224.696L413.614,160.216z"
                    />
                    <path
                      fill="#F8CF32"
                      d="M466.6,123.698l-82.969,97.209c-8.021,9.398-22.543,9.398-30.564,0l-83.816-98.2c-6.955-8.148-19.546-8.148-26.501,0l-83.816,98.2c-8.021,9.398-22.543,9.398-30.564,0L45.4,123.698c-4.831-5.66-14.097-2.244-14.097,5.198v253.625c0,7.115,5.768,12.883,12.883,12.883h423.626c7.115,0,12.884-5.768,12.884-12.883V128.896c0-7.442-9.265-10.858-14.096-5.198z"
                    />
                    <ellipse
                      opacity="0.15"
                      cx="251.44"
                      cy="285.17"
                      rx="47.607"
                      ry="65.53"
                    />
                    <circle opacity="0.15" cx="363.79" cy="285.17" r="22.95" />
                    <circle opacity="0.15" cx="139.09" cy="285.17" r="22.95" />

                    <path
                      fill="#394049"
                      d="M31.304,367.258v15.263c0,7.115,5.768,12.884,12.883,12.884h423.626c7.115,0,12.884-5.768,12.884-12.884v-15.263H31.304z"
                    />

                    <path
                      opacity="0.15"
                      d="M466.6,123.698l-3.382,3.962c0.058,0.399,0.096,0.809,0.096,1.236v90.559c-9.435,8.707-16.236,33.224-16.236,62.131s6.801,53.424,16.236,62.131v38.804c0,7.115-5.768,12.883-12.883,12.883h17.382c7.116,0,12.884-5.768,12.884-12.883V128.896c0-7.442-9.265-10.858-14.096-5.198z"
                    />

                    <ellipse fill="#1BC56E" cx="256" cy="279.64" rx="47.607" ry="65.53" />
                    <ellipse fill="#1BC56E" cx="31.3" cy="279.64" rx="23.804" ry="65.53" />
                    <ellipse fill="#1BC56E" cx="480.7" cy="279.64" rx="23.804" ry="65.53" />

                    <circle fill="#FE98BF" cx="368.35" cy="279.64" r="22.95" />
                    <circle fill="#FE98BF" cx="143.65" cy="279.64" r="22.95" />

                    <path
                      d="M398.798,279.643c0-16.79-13.659-30.45-30.449-30.45s-30.45,13.66-30.45,30.45s13.66,30.45,30.45,30.45C385.139,310.093,398.798,296.434,398.798,279.643z M352.898,279.643c0-8.519,6.931-15.45,15.45-15.45s15.449,6.931,15.449,15.45s-6.931,15.45-15.449,15.45C359.829,295.093,352.898,288.162,352.898,279.643z"
                    />

                    <path
                      d="M256,221.61c5.835,0,11.477,1.816,16.77,5.397c3.43,2.321,8.093,1.423,10.415-2.008c2.321-3.431,1.422-8.093-2.009-10.414c-7.71-5.217-16.415-7.974-25.176-7.974c-15.181,0-29.291,7.915-39.731,22.286c-9.915,13.649-15.376,31.671-15.376,50.747c0,19.076,5.461,37.098,15.376,50.747c10.44,14.372,24.551,22.286,39.731,22.286c15.18,0,29.291-7.915,39.731-22.286c9.915-13.649,15.376-31.671,15.376-50.747c0-16.665-4.084-32.338-11.809-45.325c-2.118-3.561-6.724-4.727-10.279-2.611c-3.561,2.118-4.729,6.72-2.611,10.28c6.255,10.516,9.7,23.889,9.7,37.656c0,32-17.992,58.033-40.107,58.033s-40.107-26.034-40.107-58.033S233.885,221.61,256,221.61z"
                    />

                    <path
                      d="M488.196,208.397v-79.501c0-6.566-3.981-12.278-10.143-14.549c-6.16-2.273-12.897-.512-17.159,4.482l-36.372,42.615l-37.22-43.607c-4.742-5.556-11.65-8.742-18.954-8.742c-7.305,0-14.213,3.186-18.956,8.742l-37.219,43.606l-37.219-43.606c-4.742-5.556-11.65-8.742-18.954-8.742c-7.305,0-14.213,3.186-18.956,8.742l-37.218,43.606l-37.219-43.606c-4.742-5.556-11.65-8.742-18.955-8.742c-7.304,0-14.212,3.186-18.955,8.742l-37.219,43.606L51.105,118.83c-4.264-4.996-11.003-6.754-17.159-4.483c-6.161,2.271-10.143,7.983-10.143,14.549v79.501C8.147,216.151,0,248.06,0,279.643s8.147,63.493,23.804,71.246v31.632c0,11.239,9.144,20.383,20.383,20.383h39.317c4.143,0,7.5-3.358,7.5-7.5s-3.357-7.5-7.5-7.5H44.187c-2.968,0-5.383-2.415-5.383-5.383v-31.632c15.657-7.753,23.804-39.663,23.804-71.246s-8.147-63.492-23.804-71.246v-79.501c0-.197,0-.353.331-.476c.331-.119.433-.004.561.147l82.969,97.208c5.251,6.151,12.901,9.68,20.988,9.679c8.087,0,15.736-3.528,20.986-9.679l83.814-98.2c1.916-2.244,4.596-3.48,7.547-3.48c2.95,0,5.63,1.236,7.545,3.48l83.816,98.2c5.25,6.151,12.899,9.68,20.986,9.68c8.087,0,15.736-3.528,20.987-9.679l82.969-97.209c.129-.151.232-.266.561-.146c.331.123.331.278.331.476v79.501c-15.657,7.753-23.804,39.663-23.804,71.246c0,4.142,3.357,7.5,7.5,7.5s7.5-3.358,7.5-7.5c0-16.653,2.316-32.201,6.521-43.778c3.771-10.385,8.002-14.255,9.782-14.255s6.011,3.87,9.782,14.255c4.205,11.577,6.522,27.124,6.522,43.778c0,16.653-2.316,32.201-6.522,43.778c-3.771,10.385-8.002,14.255-9.782,14.255c-2.534,0-11.102-8.807-14.827-35.772c-0.567-4.104-4.359-6.97-8.456-6.403c-4.103,0.567-6.97,4.353-6.403,8.456c3.447,24.954,11.501,41.675,22.186,46.938v31.626c0,2.968-2.415,5.383-5.384,5.383H108.498c-4.143,0-7.5,3.358-7.5,7.5s3.357,7.5,7.5,7.5h359.315c11.239,0,20.384-9.144,20.384-20.383V350.89C503.853,343.136,512,311.226,512,279.643S503.853,216.151,488.196,208.397z"
                    />
                  </svg>
                </div>
                <h3 className="text-base font-bold text-[var(--tg-theme-text-color)] mb-2 text-center">
                  Стать владельцем школы
                </h3>
                <p className="text-xs text-[var(--tg-theme-hint-color)] leading-relaxed text-center mb-4">
                  Создайте свою школу, чтобы назначать учителей и администраторов.
                  Все ваши данные сохранятся — вы просто получите роль владельца.
                </p>
                <label
                  htmlFor="upgrade-school-name"
                  className="block text-xs font-medium text-[var(--tg-theme-hint-color)] mb-2"
                >
                  Название школы
                </label>
                <input
                  id="upgrade-school-name"
                  type="text"
                  value={schoolName}
                  onChange={e => {
                    setSchoolName(e.target.value);
                    if (upgradeError) setUpgradeError(null);
                  }}
                  placeholder="Например, «Школа робототехники»"
                  maxLength={60}
                  className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] border-2 border-transparent focus:border-[var(--tg-theme-button-color)] outline-none text-sm text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)]/60 transition-all mb-3"
                />
                {upgradeError && (
                  <p role="alert" className="text-xs text-red-500 mb-3">
                    {upgradeError}
                  </p>
                )}
                <button
                  onClick={handleUpgrade}
                  disabled={upgrading}
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[var(--tg-theme-button-color)] to-purple-600 text-[var(--tg-theme-button-text-color)] font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {upgrading ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                      Создание школы...
                    </>
                  ) : (
                    // <>👑 Стать владельцем</>
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 512 512"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shrink-0"
                      >
                        <path
                          fill="#F89021"
                          d="M256,238.812l-99.098-116.105c-6.955-8.148-19.546-8.148-26.501,0L31.304,238.812H256z"
                        />
                        <path
                          opacity="0.15"
                          d="M188.917,160.216l-29.983,35.129c-8.021,9.398-22.543,9.398-30.564,0l-29.983-35.129l-67.083,78.596H256L188.917,160.216z"
                        />
                        <path
                          fill="#F89021"
                          d="M480.696,238.812l-99.098-116.105c-6.955-8.148-19.546-8.148-26.501,0L256,238.812H480.696z"
                        />
                        <path
                          opacity="0.15"
                          d="M413.614,160.216l-29.983,35.129c-8.021,9.398-22.543,9.398-30.564,0l-29.983-35.129L256,238.812h224.696L413.614,160.216z"
                        />
                        <path
                          fill="#F8CF32"
                          d="M466.6,123.698l-82.969,97.209c-8.021,9.398-22.543,9.398-30.564,0l-83.816-98.2c-6.955-8.148-19.546-8.148-26.501,0l-83.816,98.2c-8.021,9.398-22.543,9.398-30.564,0L45.4,123.698c-4.831-5.66-14.097-2.244-14.097,5.198v253.625c0,7.115,5.768,12.883,12.883,12.883h423.626c7.115,0,12.884-5.768,12.884-12.883V128.896c0-7.442-9.265-10.858-14.096-5.198z"
                        />
                        <ellipse
                          opacity="0.15"
                          cx="251.44"
                          cy="285.17"
                          rx="47.607"
                          ry="65.53"
                        />
                        <circle opacity="0.15" cx="363.79" cy="285.17" r="22.95" />
                        <circle opacity="0.15" cx="139.09" cy="285.17" r="22.95" />
                        <path
                          fill="#394049"
                          d="M31.304,367.258v15.263c0,7.115,5.768,12.884,12.883,12.884h423.626c7.115,0,12.884-5.768,12.884-12.884v-15.263H31.304z"
                        />
                        <path
                          opacity="0.15"
                          d="M466.6,123.698l-3.382,3.962c0.058,0.399,0.096,0.809,0.096,1.236v90.559c-9.435,8.707-16.236,33.224-16.236,62.131s6.801,53.424,16.236,62.131v38.804c0,7.115-5.768,12.883-12.883,12.883h17.382c7.116,0,12.884-5.768,12.884-12.883V128.896c0-7.442-9.265-10.858-14.096-5.198z"
                        />
                        <ellipse fill="#1BC56E" cx="256" cy="279.64" rx="47.607" ry="65.53" />
                        <ellipse fill="#1BC56E" cx="31.3" cy="279.64" rx="23.804" ry="65.53" />
                        <ellipse fill="#1BC56E" cx="480.7" cy="279.64" rx="23.804" ry="65.53" />
                        <circle fill="#FE98BF" cx="368.35" cy="279.64" r="22.95" />
                        <circle fill="#FE98BF" cx="143.65" cy="279.64" r="22.95" />
                        <path
                          d="M398.798 279.643c0-16.79-13.659-30.45-30.449-30.45s-30.45 13.66-30.45 30.45 13.66 30.45 30.45 30.45c16.79 0 30.449-13.659 30.449-30.45zM352.898 279.643c0-8.519 6.931-15.45 15.45-15.45s15.449 6.931 15.449 15.45-6.931 15.45-15.449 15.45c-8.519 0-15.45-6.931-15.45-15.45z"
                        />
                        <path
                          d="M256 221.61c5.835 0 11.477 1.816 16.77 5.397 3.43 2.321 8.093 1.423 10.415-2.008 2.321-3.431 1.422-8.093-2.009-10.414-7.71-5.217-16.415-7.974-25.176-7.974-15.181 0-29.291 7.915-39.731 22.286-9.915 13.649-15.376 31.671-15.376 50.747 0 19.076 5.461 37.098 15.376 50.747 10.44 14.372 24.551 22.286 39.731 22.286 15.18 0 29.291-7.915 39.731-22.286 9.915-13.649 15.376-31.671 15.376-50.747 0-16.665-4.084-32.338-11.809-45.325-2.118-3.561-6.724-4.727-10.279-2.611-3.561 2.118-4.729 6.72-2.611 10.28 6.255 10.516 9.7 23.889 9.7 37.656 0 32-17.992 58.033-40.107 58.033s-40.107-26.034-40.107-58.033S233.885 221.61 256 221.61z"
                        />
                        <path
                          d="M488.196 208.397v-79.501c0-6.566-3.981-12.278-10.143-14.549-6.16-2.273-12.897-.512-17.159 4.482l-36.372 42.615-37.22-43.607c-4.742-5.556-11.65-8.742-18.954-8.742-7.305 0-14.213 3.186-18.956 8.742l-37.219 43.606-37.219-43.606c-4.742-5.556-11.65-8.742-18.954-8.742-7.305 0-14.213 3.186-18.956 8.742l-37.218 43.606-37.219-43.606c-4.742-5.556-11.65-8.742-18.955-8.742-7.304 0-14.212 3.186-18.955 8.742l-37.219 43.606L51.105 118.83c-4.264-4.996-11.003-6.754-17.159-4.483-6.161 2.271-10.143 7.983-10.143 14.549v79.501C8.147 216.151 0 248.06 0 279.643s8.147 63.493 23.804 71.246v31.632c0 11.239 9.144 20.383 20.383 20.383h39.317c4.143 0 7.5-3.358 7.5-7.5s-3.357-7.5-7.5-7.5H44.187c-2.968 0-5.383-2.415-5.383-5.383v-31.632c15.657-7.753 23.804-39.663 23.804-71.246s-8.147-63.492-23.804-71.246v-79.501c0-.197 0-.353.331-.476.331-.119.433-.004.561.147l82.969 97.208c5.251 6.151 12.901 9.68 20.988 9.679 8.087 0 15.736-3.528 20.986-9.679l83.814-98.2c1.916-2.244 4.596-3.48 7.547-3.48 2.95 0 5.63 1.236 7.545 3.48l83.816 98.2c5.25 6.151 12.899 9.68 20.986 9.68 8.087 0 15.736-3.528 20.987-9.679l82.969-97.209c.129-.151.232-.266.561-.146.331.123.331.278.331.476v79.501c-15.657 7.753-23.804 39.663-23.804 71.246 0 4.142 3.357 7.5 7.5 7.5s7.5-3.358 7.5-7.5c0-16.653 2.316-32.201 6.521-43.778 3.771-10.385 8.002-14.255 9.782-14.255s6.011 3.87 9.782 14.255c4.205 11.577 6.522 27.124 6.522 43.778 0 16.653-2.316 32.201-6.522 43.778-3.771 10.385-8.002 14.255-9.782 14.255-2.534 0-11.102-8.807-14.827-35.772-0.567-4.104-4.359-6.97-8.456-6.403-4.103 0.567-6.97 4.353-6.403 8.456 3.447 24.954 11.501 41.675 22.186 46.938v31.626c0 2.968-2.415 5.383-5.384 5.383H108.498c-4.143 0-7.5 3.358-7.5 7.5s3.357 7.5 7.5 7.5h359.315c11.239 0 20.384-9.144 20.384-20.383V350.89C503.853 343.136 512 311.226 512 279.643s-8.147-63.492-23.804-71.246z"
                        />
                      </svg>
                      Стать владельцем
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Тариф и подписка ──────────────────────────────────────── */}
      <div className="tg-card">
        <h3 className="text-base font-semibold text-[var(--tg-theme-text-color)] mb-1 flex items-center gap-2">
          <span><img src="/icons/тариф-и-подписка.svg" alt="star" width="18" height="18" className="object-contain" /></span> Тариф и подписка
        </h3>
        <p className="text-xs text-[var(--tg-theme-hint-color)] leading-relaxed mb-3">
          {isSysOwner
            ? '👑 Все платные функции доступны без ограничений'
            : myPlan
              ? `Ваш тариф: ${myPlan}`
              : 'Планы, оплата, пробный период'
          }
        </p>
        <button
          onClick={() => navigate('/billing')}
          className="w-full py-2.5 rounded-xl text-sm font-semibold text-[var(--tg-theme-button-color)] bg-[var(--tg-theme-secondary-bg-color)] transition-all hover:opacity-80 active:scale-[0.98] flex items-center justify-center gap-2"
        >
          Открыть
        </button>
        {permissions.canOpenOwnerPanel && (
          <button
            onClick={() => navigate('/owner')}
            className="w-full mt-2 py-2.5 rounded-xl text-sm font-semibold text-[var(--tg-theme-button-color)] bg-[var(--tg-theme-secondary-bg-color)] transition-all hover:opacity-80 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <span>⚙️</span> Панель владельца
          </button>
        )}
      </div>

      {/* ── Настройки (сворачиваемая секция) ────────────────────── */}
      <div className="tg-card">
        <button
          onClick={() => setSettingsOpen(!settingsOpen)}
          className="w-full flex items-center justify-between text-left"
        >
          {/* <h3 className="text-base font-semibold text-[var(--tg-theme-text-color)] flex items-center gap-2">
            <span>⚙️</span> {t('account.settings')}
          </h3> */}
          <h3 className="text-base font-semibold text-[var(--tg-theme-text-color)] flex items-center gap-2">
            <svg
              width="20"
              height="20"
              viewBox="0 0 294.844 294.844"
              fill="currentColor"
              className="shrink-0"
            >
              <path d="M240.15 201.967c-2.342-2.344-6.143-2.344-8.484 0-2.344 2.343-2.344 6.142 0 8.484l11.663 11.664c2.793 2.793 4.332 6.507 4.332 10.457 0 3.951-1.539 7.665-4.332 10.459l-.297.297c-2.794 2.793-6.508 4.331-10.459 4.331-3.95 0-7.664-1.538-10.458-4.332l-11.663-11.662c-2.021-2.02-5.186-2.336-7.567-.751-10.885 7.246-22.876 12.218-35.642 14.777-2.803.562-4.82 3.023-4.82 5.883v16.479c0 8.156-6.635 14.791-14.79 14.791h-.42c-8.155 0-14.79-6.635-14.79-14.791v-16.479c0-2.859-2.017-5.321-4.821-5.883-12.766-2.56-24.757-7.531-35.642-14.777-2.38-1.584-5.546-1.27-7.568.751l-11.663 11.663c-2.793 2.793-6.508 4.331-10.458 4.331-3.951 0-7.665-1.538-10.458-4.331l-.297-.297c-2.793-2.794-4.332-6.508-4.332-10.459 0-3.95 1.539-7.664 4.332-10.458l11.663-11.663c2.021-2.021 2.336-5.188.752-7.567-7.247-10.885-12.219-22.877-14.779-35.642-.562-2.803-3.024-4.82-5.883-4.82H26.791c-8.155 0-14.79-6.635-14.79-14.789v-.421c0-8.155 6.635-14.79 14.79-14.79h16.479c2.859 0 5.321-2.017 5.883-4.82 2.561-12.767 7.533-24.758 14.779-35.642 1.584-2.38 1.27-5.547-.752-7.568L51.516 72.73c-2.793-2.794-4.332-6.508-4.332-10.459s1.539-7.665 4.332-10.459l.297-.297c2.793-2.793 6.507-4.332 10.458-4.332 3.95 0 7.665 1.539 10.458 4.333l11.663 11.662c2.344 2.342 6.143 2.343 8.485-.001 2.343-2.343 2.343-6.142 0-8.485L81.215 43.031c-5.06-5.061-11.788-7.848-18.943-7.848-7.156 0-13.884 2.787-18.943 7.848l-.297.297c-5.06 5.06-7.847 11.787-7.847 18.943s2.787 13.884 7.847 18.944l8.264 8.263c-5.837 9.651-10.139 20.024-12.832 30.943H26.791c-14.772 0-26.79 12.018-26.79 26.79v.421c0 14.771 12.018 26.789 26.79 26.789h11.672c2.692 10.919 6.993 21.29 12.832 30.943l-8.263 8.264c-5.061 5.06-7.847 11.787-7.847 18.943s2.787 13.884 7.847 18.943l.297.297c5.06 5.061 11.787 7.847 18.943 7.847 7.156 0 13.883-2.786 18.943-7.846l8.264-8.264c9.653 5.838 20.024 10.139 30.944 12.831v11.672c0 14.772 12.018 26.791 26.79 26.791h.42c14.772 0 26.79-12.019 26.79-26.791v-11.672c10.92-2.692 21.291-6.993 30.943-12.831l8.264 8.263c5.06 5.061 11.787 7.847 18.943 7.847s13.884-2.786 18.943-7.847l.297-.297c5.061-5.06 7.848-11.787 7.848-18.943s-2.787-13.884-7.848-18.942l-11.663-11.664z" />

              <path d="M268.053 120.422H256.38c-2.692-10.92-6.994-21.292-12.831-30.943l8.264-8.264c10.446-10.445 10.446-27.441 0-37.887l-.297-.297c-5.06-5.061-11.787-7.848-18.943-7.848s-13.884 2.787-18.942 7.847l-8.265 8.265c-9.652-5.838-20.024-10.139-30.943-12.831V26.791C174.422 12.019 162.405 0 147.632 0h-.42c-14.772 0-26.79 12.019-26.79 26.791V43.27c0 3.313 2.687 6 6 6 3.314 0 6-2.687 6-6V26.791c0-8.156 6.635-14.791 14.79-14.791h.42c8.155 0 14.79 6.635 14.79 14.791V43.27c0 2.858 2.018 5.32 4.82 5.883 12.766 2.561 24.757 7.532 35.642 14.778 2.378 1.582 5.545 1.27 7.567-.752l11.664-11.663c2.793-2.793 6.507-4.332 10.457-4.332 3.951 0 7.665 1.539 10.459 4.332l.297.297c2.793 2.794 4.332 6.508 4.332 10.459s-1.539 7.665-4.332 10.458l-11.663 11.662c-2.021 2.021-2.337 5.188-.752 7.568 7.245 10.883 12.218 22.874 14.778 35.642.563 2.803 3.024 4.82 5.883 4.82h16.479c8.155 0 14.79 6.635 14.79 14.79v.421c0 8.154-6.635 14.789-14.79 14.789h-16.479c-3.313 0-6 2.687-6 6s2.687 6 6 6h16.479c14.772 0 26.79-12.018 26.79-26.789v-.421c0-14.772-12.018-26.79-26.79-26.79z" />

              <path d="M147.422 170.024c-5.241 0-10.158-1.74-14.22-5.033-2.573-2.084-6.352-1.691-8.439.883-2.086 2.574-1.691 6.353.883 8.439 6.134 4.973 13.868 7.711 21.776 7.711 19.079 0 34.602-15.522 34.602-34.603 0-19.079-15.522-34.602-34.602-34.602-19.08 0-34.602 15.522-34.602 34.602 0 3.313 2.687 6 6 6s6-2.687 6-6c0-12.463 10.139-22.602 22.602-22.602s22.602 10.139 22.602 22.602-10.139 22.603-22.602 22.603z" />
            </svg>

            {t('account.settings')}
          </h3>
          <svg
            width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            className={`text-[var(--tg-theme-hint-color)] transition-transform duration-200 ${settingsOpen ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {settingsOpen && (
          <div className="mt-3 space-y-0.5">
            {/* Пункты: уведомления, тема */}
            {settingsItems.map((item, i) => (
              <button
                key={i}
                onClick={item.action}
                disabled={item.label === 'Уведомления' && !notificationsSupported}
                className={`w-full flex items-center gap-3 py-3 px-2 rounded-xl transition-all group ${
                  item.label === 'Уведомления' && !notificationsSupported
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-[var(--tg-theme-secondary-bg-color)] active:scale-[0.98]'
                }`}
              >
                <div className="w-9 h-9 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-base shrink-0 group-hover:scale-105 transition-transform">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <span className="text-sm font-medium text-[var(--tg-theme-text-color)] block">
                    {item.label}
                  </span>
                  <span className="text-[11px] text-[var(--tg-theme-hint-color)]">
                    {item.description}
                  </span>
                </div>
                {item.type === 'toggle' ? (
                  <div className={`w-11 h-6 rounded-full transition-all duration-200 shrink-0 relative ${
                    item.value
                      ? 'bg-[var(--tg-theme-button-color)]'
                      : 'bg-[var(--tg-theme-section-separator-color)]'
                  }`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-all duration-200 ${
                      item.value ? 'left-[22px]' : 'left-0.5'
                    }`} />
                  </div>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--tg-theme-hint-color)] shrink-0">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                )}
              </button>
            ))}

            {/* ── Часовой пояс ──────────────────────────────────────── */}
            <div className="pt-4 mt-4 border-t border-[var(--tg-theme-section-separator-color)]">
              <h4 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-1 flex items-center gap-2">
                {/* <span>🕐</span> {t('account.timezone')} */}
                <span><img src="/icons/timezone.svg" alt="timezone" width="18" height="18" className="object-contain" /></span> {t('account.timezone')}
              </h4>
              <p className="text-[11px] text-[var(--tg-theme-hint-color)] mb-3">
                Время занятий и отсчёт до начала показываются в вашем поясе. По умолчанию определяется автоматически.{user?.role === 'owner_school' ? ' Для владельца школы этот пояс также станет поясом всей школы.' : ''}
              </p>

              <div className="flex items-center justify-between rounded-xl bg-[var(--tg-theme-secondary-bg-color)] px-3 py-2.5 mb-2">
                <span className="text-sm text-[var(--tg-theme-text-color)]">Сейчас у вас</span>
                <span className="text-sm font-semibold text-[var(--tg-theme-button-color)]">
                  {localTime}
                  <span className="ml-1.5 text-[10px] font-normal text-[var(--tg-theme-hint-color)]">{detectedTz || 'не определён'}</span>
                </span>
              </div>

              <select
                value={selectedTz}
                onChange={e => setSelectedTz(e.target.value)}
                className="w-full rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm text-[var(--tg-theme-text-color)] px-3 py-2.5 outline-none mb-2"
              >
                <option value="">🌐 Авто (текущий пояс устройства)</option>
                {TZ_OPTIONS.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveTimezone}
                  disabled={savingTz}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm active:scale-[0.98] transition-all disabled:opacity-60"
                  style={{ backgroundColor: 'var(--tg-theme-button-color)' }}
                >
                  {savingTz ? 'Сохраняю…' : 'Сохранить'}
                </button>
                {tzMsg && <span className="text-xs text-green-600 shrink-0">{tzMsg}</span>}
              </div>
            </div>

            {/* ── Язык ───────────────────────────────────────────────── */}
            <div className="pt-4 mt-4 border-t border-[var(--tg-theme-section-separator-color)]">
              <h4 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-1 flex items-center gap-2">
                {/* <span>🌐</span> {t('account.language')} */}
                <span><img src="/icons/language.svg" alt="language" width="18" height="18" className="object-contain" /></span> {t('account.language')}
              </h4>
              <p className="text-[11px] text-[var(--tg-theme-hint-color)] mb-3">
                Язык интерфейса. Если выбран вручную, он не изменится при смене языка Telegram.
              </p>

              <select
                value={selectedLang}
                onChange={e => setSelectedLang(e.target.value)}
                className="w-full rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm text-[var(--tg-theme-text-color)] px-3 py-2.5 outline-none mb-2"
              >
                {SUPPORTED_LANGUAGES.map(l => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveLanguage}
                  disabled={savingLang}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm active:scale-[0.98] transition-all disabled:opacity-60"
                  style={{ backgroundColor: 'var(--tg-theme-button-color)' }}
                >
                  {savingLang ? 'Сохраняю…' : 'Сохранить'}
                </button>
                {langMsg && <span className="text-xs text-green-600 shrink-0">{langMsg}</span>}
              </div>
            </div>

            {/* ── Валюта ─────────────────────────────────────────────── */}
            <div className="pt-4 mt-4 border-t border-[var(--tg-theme-section-separator-color)]">
              <h4 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-1 flex items-center gap-2">
                {/* <span>💱</span> {t('account.currency')} */}
                <span><img src="/icons/currency.svg" alt="currency" width="18" height="18" className="object-contain" /></span> {t('account.currency')}

              </h4>
              <p className="text-[11px] text-[var(--tg-theme-hint-color)] mb-3">
                Валюта курсов и ученических платежей. Выбирается один раз и применяется ко всем курсам и платежам.
              </p>

              <select
                value={selectedCurrency}
                onChange={e => setSelectedCurrency(e.target.value)}
                className="w-full rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm text-[var(--tg-theme-text-color)] px-3 py-2.5 outline-none mb-2"
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSaveCurrency}
                  disabled={savingCurrency}
                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm active:scale-[0.98] transition-all disabled:opacity-60"
                  style={{ backgroundColor: 'var(--tg-theme-button-color)' }}
                >
                  {savingCurrency ? 'Сохраняю…' : 'Сохранить'}
                </button>
                {currencyMsg && <span className="text-xs text-green-600 shrink-0">{currencyMsg}</span>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── About ──────────────────────────────────────────────────── */}
      <div className="tg-card">
        {/* <h3 className="text-base font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <span>📱</span> {t('account.about')}
        </h3> */}
        <h3 className="text-base font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <svg
            width="20"
            height="20"
            viewBox="0 0 100 100"
            fill="currentColor"
            className="shrink-0"
          >
            <path d="M50 13.591a36.409 36.409 0 1 0 36.409 36.409A36.45 36.45 0 0 0 50 13.591zm0 68.818a32.409 32.409 0 1 1 32.409-32.409A32.446 32.446 0 0 1 50 82.409z" />

            <path d="M50 3a47 47 0 1 0 47 47A47.053 47.053 0 0 0 50 3zm0 90a43 43 0 1 1 43-43A43.049 43.049 0 0 1 50 93z" />

            <path d="M50 43.747h-4.82a2 2 0 1 0 0 4h2.82v19.723a2 2 0 0 0 4 0V45.747a2 2 0 0 0-2-2z" />

            <path d="M51.11 34.82a1.633 1.633 0 0 0-.34-.19 1.815 1.815 0 0 0-.38-.11 1.994 1.994 0 0 0-1.8.55 1.966 1.966 0 0 0-.59 1.41 1.722 1.722 0 0 0 .04.39 1.577 1.577 0 0 0 .11.37 1.954 1.954 0 0 0 .19.35 1.425 1.425 0 0 0 .25.3 1.966 1.966 0 0 0 1.41.59 1.722 1.722 0 0 0 .39-.04 2.719 2.719 0 0 0 .38-.11 2.268 2.268 0 0 0 .34-.19 1.789 1.789 0 0 0 .31-.25 1.755 1.755 0 0 0 .24-.3 1.954 1.954 0 0 0 .19-.35 2.259 2.259 0 0 0 .11-.37 1.722 1.722 0 0 0 .04-.39 2 2 0 0 0-.58-1.41 1.789 1.789 0 0 0-.31-.25z" />
          </svg>

          {t('account.about')}
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-[var(--tg-theme-hint-color)]">Версия</span>
            <span className="text-sm font-medium text-[var(--tg-theme-text-color)]">1.0.0</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-[var(--tg-theme-hint-color)]">Платформа</span>
            <span className="text-sm font-medium text-[var(--tg-theme-text-color)]">Telegram Mini App</span>
          </div>
          <div className="pt-3 mt-1 border-t border-[var(--tg-theme-section-separator-color)] space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-[var(--tg-theme-hint-color)]">Commit</span>
              <span className="text-xs font-mono text-[var(--tg-theme-text-color)] opacity-70">{__APP_COMMIT__}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[var(--tg-theme-hint-color)]">Build</span>
              <span className="text-xs font-mono text-[var(--tg-theme-text-color)] opacity-70">{__APP_BUILD_TIME__}</span>
            </div>
          </div>

          <div className="pt-3 mt-1 border-t border-[var(--tg-theme-section-separator-color)] space-y-2">
            <button
              onClick={() => navigate('/privacy')}
              className="w-full flex items-center justify-between py-2 text-sm text-[var(--tg-theme-text-color)] hover:opacity-80 transition-opacity"
            >
              {/* <span className="flex items-center gap-2"><img src="/icons/политика-конфиденциальности.svg" alt="" width="18" height="18" className="object-contain" /> Политика конфиденциальности</span>
              <span className="text-[var(--tg-theme-hint-color)]">›</span> */}
              <span className="flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 28 28"
                  width="18"
                  height="18"
                  className="object-contain text-[var(--tg-theme-text-color)]"
                  fill="currentColor"
                >
                  <g>
                    {<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28">
                      <g id="Layer_2" data-name="Layer 2" fill="currentColor">
                        <path d="M13.074,21h-1.5V19.5a.5.5,0,0,0-.854-.354L9.574,20.293V19.5a.5.5,0,0,0-.309-.462.505.505,0,0,0-.545.108l-2,2a.5.5,0,1,0,.707.708l1.147-1.147V21.5a.5.5,0,0,0,.308.462A.513.513,0,0,0,9.074,22a.5.5,0,0,0,.353-.146l1.147-1.147V21.5a.5.5,0,0,0,.5.5h2a.5.5,0,0,0,0-1Z"/>
                        <path d="M7.074,11H13a.5.5,0,0,0,0-1H7.074a.5.5,0,1,0,0,1Z"/>
                        <path d="M7.074,14H13a.5.5,0,0,0,0-1H7.074a.5.5,0,1,0,0,1Z"/>
                        <path d="M11.074,8h5a.5.5,0,0,0,0-1h-5a.5.5,0,1,0,0,1Z"/>
                        <path d="M25.186,11.5,20.574,9.658V3.884A1.886,1.886,0,0,0,18.69,2H8.074a.5.5,0,0,0-.354.146l-5,5a.5.5,0,0,0-.146.354V24.116A1.886,1.886,0,0,0,4.458,26H18.69a1.886,1.886,0,0,0,1.884-1.884v-2.05l2.57-1.8A5.53,5.53,0,0,0,25.5,15.742V11.967A.5.5,0,0,0,25.186,11.5ZM7.574,3.707V7H4.281ZM18.69,25H4.458a.884.884,0,0,1-.884-.884V8h4.5a.5.5,0,0,0,.5-.5V3H18.69a.884.884,0,0,1,.884.884V9.665L14.981,11.5a.5.5,0,0,0-.314.464v3.775a5.534,5.534,0,0,0,2.355,4.526l2.552,1.786v2.062A.884.884,0,0,1,18.69,25Zm5.81-9.258a4.529,4.529,0,0,1-1.931,3.707l-2.486,1.74L17.6,19.449a4.53,4.53,0,0,1-1.93-3.707V12.306l4.416-1.768L24.5,12.306Z"/>
                        <path d="M18.354,14.979a.5.5,0,1,0-.708.708l1.333,1.334a.5.5,0,0,0,.708,0l2.667-2.667a.5.5,0,0,0-.708-.708L19.333,15.96Z"/>
                      </g>
                    </svg>}
                  </g>
                </svg>

                Политика конфиденциальности
              </span>

              <span className="text-[var(--tg-theme-hint-color)]">›</span>
            </button>
            <button
              onClick={() => navigate('/terms')}
              className="w-full flex items-center justify-between py-2 text-sm text-[var(--tg-theme-text-color)] hover:opacity-80 transition-opacity"
            >
              {/* <span className="flex items-center gap-2"><img src="/icons/условия-использования.svg" alt="" width="18" height="18" className="object-contain" /> Условия использования</span>
              <span className="text-[var(--tg-theme-hint-color)]">›</span> */}
              <span className="flex items-center gap-2">
                <span
                  className="w-[18px] h-[18px] bg-[var(--tg-theme-text-color)]"
                  style={{
                    maskImage: 'url("/icons/условия-использования.svg")',
                    WebkitMaskImage: 'url("/icons/условия-использования.svg")',
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskPosition: 'center',
                    WebkitMaskPosition: 'center',
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                  }}
                />
                Условия использования
              </span>

              <span className="text-[var(--tg-theme-hint-color)]">›</span>
            </button>
          </div>

          <div className="pt-3 mt-1 border-t border-[var(--tg-theme-section-separator-color)]">
            <p className="text-center text-[11px] text-[var(--tg-theme-hint-color)]">
              Создано при поддержке <a href="https://github.com/AngelOK-5725" target="_blank" rel="noopener noreferrer" className="font-semibold text-[var(--tg-theme-link-color)] hover:underline">AngelOK</a>
            </p>
          </div>
        </div>
      </div>

      {/* ── Logout ─────────────────────────────────────────────────── */}
      <button
        onClick={logout}
        className="w-full py-3 rounded-2xl border border-red-200 text-red-500 font-semibold text-sm hover:bg-red-50 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
        Выйти из аккаунта
      </button>
    </div>
  );
}
